import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActionButton, Flex, ProgressCircle, Text, View } from '@adobe/react-spectrum';
import { ToastQueue } from '@adobe/react-spectrum';
import { ChevronLeft, ChevronRight, Download, FileEarmarkArrowDown, FileEarmarkText, Send, Trash, Upload } from 'react-bootstrap-icons';
import { getDownloadUrl } from '../../../../utils/ImagePathUtils';
import { openPdfContentInNewWindow, openUrlInNewWindow } from '../../../../utils/NavigationUtils';
import * as pdfjsLib from 'pdfjs-dist';
import styles from './document_preview.module.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

export type PreviewDocument = {
  documentId: string;
  name: string;
  fileName?: string;
  mimeContentType?: string;
};

interface IDocumentPreviewComponentProps {
  documents: PreviewDocument[];
  baseUrl: string;
  server: string;
  session: string;
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  onSendDocument?: (document: PreviewDocument) => Promise<void> | void;
  onUploadDocument?: () => void;
  onDeleteDocument?: (document: PreviewDocument) => Promise<void> | void;
}

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
const textExtensions = ['txt', 'json', 'csv', 'xml', 'log'];

const getExtension = (name?: string) => String(name || '').split('.').pop()?.toLowerCase() || '';

const detectMimeTypeFromBlob = async (blob: Blob): Promise<string> => {
  const buffer = await blob.slice(0, 32).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  const startsWith = (signature: number[]) => signature.every((value, index) => bytes[index] === value);

  if (startsWith([0x25, 0x50, 0x44, 0x46])) return 'application/pdf';
  if (startsWith([0x89, 0x50, 0x4e, 0x47])) return 'image/png';
  if (startsWith([0xff, 0xd8, 0xff])) return 'image/jpeg';
  if (startsWith([0x47, 0x49, 0x46, 0x38])) return 'image/gif';
  if (startsWith([0x42, 0x4d])) return 'image/bmp';
  if (startsWith([0x52, 0x49, 0x46, 0x46]) && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
    return 'image/webp';
  }

  const textSample = await blob.slice(0, 512).text().catch(() => '');
  const trimmed = textSample.trim().toLowerCase();
  if (trimmed.startsWith('<svg') || trimmed.includes('<svg')) return 'image/svg+xml';
  const isLikelyText = textSample.length > 0 && Array.from(textSample).every((character) => {
    const code = character.charCodeAt(0);
    return code === 9 || code === 10 || code === 13 || code >= 32;
  });
  if (trimmed && isLikelyText) return 'text/plain';

  return blob.type || '';
};

const getDocumentKind = (document?: PreviewDocument, mimeOverride?: string) => {
  if (!document) return 'unknown';

  const mime = String(mimeOverride || document.mimeContentType || '').toLowerCase();
  const extension = getExtension(document.fileName || document.name);

  if (mime.includes('pdf') || extension === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || imageExtensions.includes(extension)) return 'image';
  if (mime.startsWith('text/') || textExtensions.includes(extension)) return 'text';
  return 'other';
};

const DocumentPreviewComponent: React.FC<IDocumentPreviewComponentProps> = ({
  documents,
  baseUrl,
  server,
  session,
  selectedIndex,
  onSelectedIndexChange,
  onSendDocument,
  onUploadDocument,
  onDeleteDocument,
}) => {
  const [internalIndex, setInternalIndex] = useState(0);
  const [textPreview, setTextPreview] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [resolvedMimeType, setResolvedMimeType] = useState('');
  const [resolvedBlobUrl, setResolvedBlobUrl] = useState('');
  const [resolvedArrayBuffer, setResolvedArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [pdfRenderError, setPdfRenderError] = useState(false);
  const pdfCanvasContainerRef = useRef<HTMLDivElement | null>(null);
  const previewScrollAreaRef = useRef<HTMLDivElement | null>(null);
  const currentIndex = typeof selectedIndex === 'number' ? selectedIndex : internalIndex;

  useEffect(() => {
    if (currentIndex >= documents.length && documents.length > 0) {
      if (typeof selectedIndex === 'number') {
        onSelectedIndexChange?.(0);
      } else {
        setInternalIndex(0);
      }
    }
  }, [currentIndex, documents.length, onSelectedIndexChange, selectedIndex]);

  const selectedDocument = documents[currentIndex];
  const documentKind = useMemo(() => getDocumentKind(selectedDocument, resolvedMimeType), [resolvedMimeType, selectedDocument]);
  const documentUrl = selectedDocument?.documentId
    ? getDownloadUrl(selectedDocument.documentId, baseUrl, server, session)
    : '';

  useEffect(() => {
    let isActive = true;
    let objectUrl = '';

    const loadPreview = async () => {
      if (!selectedDocument || !documentUrl) {
        setTextPreview('');
        setResolvedMimeType('');
        setResolvedBlobUrl('');
        return;
      }

      setIsLoadingPreview(true);
      try {
        const response = await fetch(documentUrl);
        const blob = await response.blob();
        const mimeType = String((await detectMimeTypeFromBlob(blob)) || response.headers.get('content-type') || '').toLowerCase();
        const arrayBuffer = await blob.arrayBuffer();
        objectUrl = URL.createObjectURL(blob);
        const kind = getDocumentKind(selectedDocument, mimeType);

        if (!isActive) return;

        setResolvedMimeType(mimeType);
        setResolvedBlobUrl(objectUrl);
        setResolvedArrayBuffer(arrayBuffer);
        setPdfRenderError(false);

        if (kind === 'text') {
          const content = await blob.text();
          if (isActive) {
            setTextPreview(content);
          }
        } else {
          setTextPreview('');
        }
      } catch {
        if (isActive) {
          setTextPreview('');
          setResolvedMimeType('');
          setResolvedBlobUrl('');
          setResolvedArrayBuffer(null);
        }
      } finally {
        if (isActive) {
          setIsLoadingPreview(false);
        }
      }
    };

    loadPreview();
    return () => {
      isActive = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentUrl, selectedDocument]);

  useEffect(() => {
    let isCancelled = false;

    const renderPdfPreview = async () => {
      if (documentKind !== 'pdf' || !resolvedArrayBuffer || !pdfCanvasContainerRef.current) {
        return;
      }

      try {
        const container = pdfCanvasContainerRef.current;
        container.innerHTML = '';

        const pdfDocument = await pdfjsLib.getDocument({ data: resolvedArrayBuffer.slice(0) }).promise;
        const containerWidth = Math.max(container.clientWidth - 32, 300);

        for (let pageIndex = 1; pageIndex <= pdfDocument.numPages; pageIndex += 1) {
          if (isCancelled) return;
          const page = await pdfDocument.getPage(pageIndex);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = containerWidth / baseViewport.width;
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;

          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.display = 'block';
          canvas.style.margin = '0 auto 16px auto';
          canvas.style.background = '#fff';
          canvas.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';

          await page.render({
            canvasContext: context,
            viewport,
          }).promise;

          if (!isCancelled) {
            container.appendChild(canvas);
          }
        }
      } catch {
        if (!isCancelled) {
          setPdfRenderError(true);
        }
      }
    };

    renderPdfPreview();

    return () => {
      isCancelled = true;
    };
  }, [documentKind, resolvedArrayBuffer]);

  const setIndex = (nextIndex: number) => {
    if (typeof selectedIndex === 'number') {
      onSelectedIndexChange?.(nextIndex);
    } else {
      setInternalIndex(nextIndex);
    }
  };

  const openDocument = async () => {
    if (!selectedDocument || !documentUrl) return;

    if (documentKind === 'pdf') {
      try {
        const response = await fetch(documentUrl);
        const file = await response.arrayBuffer();
        await openPdfContentInNewWindow(file);
        return;
      } catch {
        ToastQueue.negative('Unable to open PDF preview.', { timeout: 2000 });
        return;
      }
    }

    openUrlInNewWindow(documentUrl);
  };

  const downloadDocument = () => {
    if (!documentUrl) return;
    openUrlInNewWindow(documentUrl);
  };

  const sendDocument = async () => {
    if (!selectedDocument || !onSendDocument) return;
    await onSendDocument(selectedDocument);
  };

  if (!documents.length) {
    return (
      <View UNSAFE_className={styles.preview_shell}>
        <View UNSAFE_className={styles.toolbar}>
          <View UNSAFE_className={styles.toolbar_left}>
            <div className={styles.document_name}>No documents available</div>
          </View>
          <View UNSAFE_className={styles.toolbar_center}>
            <span className={styles.pager_badge}>0</span>
            <span className={styles.pager_total}>of 0</span>
          </View>
          <View UNSAFE_className={styles.toolbar_actions}>
            {onUploadDocument ? (
              <ActionButton UNSAFE_className={styles.icon_button} onPress={onUploadDocument}>
                <Upload size={16} />
              </ActionButton>
            ) : null}
          </View>
        </View>
        <View UNSAFE_className={styles.empty_state}>No documents available.</View>
      </View>
    );
  }

  return (
    <View UNSAFE_className={styles.preview_shell}>
      <View UNSAFE_className={styles.toolbar}>
        <View UNSAFE_className={styles.toolbar_left}>
          <div className={styles.document_name}>{selectedDocument?.name || selectedDocument?.fileName || '-'}</div>
        </View>

        <View UNSAFE_className={styles.toolbar_center}>
          <ActionButton
            isQuiet
            UNSAFE_className={styles.toolbar_pager_button}
            onPress={() => setIndex(Math.max(0, currentIndex - 1))}
            isDisabled={currentIndex === 0}
          >
            <ChevronLeft size={16} />
          </ActionButton>
          <span className={styles.pager_badge}>{currentIndex + 1}</span>
          <span className={styles.pager_total}>of {documents.length}</span>
          <ActionButton
            isQuiet
            UNSAFE_className={styles.toolbar_pager_button}
            onPress={() => setIndex(Math.min(documents.length - 1, currentIndex + 1))}
            isDisabled={currentIndex >= documents.length - 1}
          >
            <ChevronRight size={16} />
          </ActionButton>
        </View>

        <View UNSAFE_className={styles.toolbar_actions}>
          {onUploadDocument ? (
            <ActionButton UNSAFE_className={styles.icon_button} onPress={onUploadDocument}>
              <Upload size={16} />
            </ActionButton>
          ) : null}
          <ActionButton UNSAFE_className={styles.icon_button} onPress={downloadDocument}>
            <Download size={16} />
          </ActionButton>
          {onSendDocument ? (
            <ActionButton UNSAFE_className={styles.icon_button} onPress={sendDocument}>
              <Send size={16} />
            </ActionButton>
          ) : null}
          {onDeleteDocument ? (
            <ActionButton UNSAFE_className={styles.icon_button} onPress={() => selectedDocument && onDeleteDocument(selectedDocument)}>
              <Trash size={16} />
            </ActionButton>
          ) : null}
          <ActionButton UNSAFE_className={styles.text_button} onPress={openDocument}>
            <Flex alignItems="center" gap="size-75">
              <FileEarmarkArrowDown size={16} />
              <Text>{documentKind === 'pdf' ? 'Open as PDF' : 'Open file'}</Text>
            </Flex>
          </ActionButton>
        </View>
      </View>

      <View UNSAFE_className={styles.preview_body}>
        <View UNSAFE_className={styles.preview_frame}>
          <div ref={previewScrollAreaRef} className={styles.preview_scroll_area}>
            {isLoadingPreview ? (
              <Flex width="100%" height="380px" alignItems="center" justifyContent="center">
                <ProgressCircle aria-label="Loading document preview" isIndeterminate />
              </Flex>
            ) : null}

            {!isLoadingPreview && documentKind === 'pdf' ? (
              pdfRenderError && resolvedBlobUrl ? (
                <iframe title={selectedDocument?.name || 'PDF preview'} src={resolvedBlobUrl} className={styles.pdf_frame} />
              ) : (
                <div ref={pdfCanvasContainerRef} className={styles.pdf_canvas_container} />
              )
            ) : null}

            {!isLoadingPreview && documentKind === 'image' && resolvedBlobUrl ? (
              <img
                src={resolvedBlobUrl}
                alt={selectedDocument?.name || 'Document preview'}
                className={styles.image_preview}
              />
            ) : null}

            {!isLoadingPreview && documentKind === 'text' ? (
              <pre className={styles.text_preview}>{textPreview || 'Unable to preview this text document.'}</pre>
            ) : null}

            {!isLoadingPreview && documentKind === 'other' ? (
              <View UNSAFE_className={styles.fallback_preview}>
                <FileEarmarkText size={42} />
                <div className={styles.fallback_title}>{selectedDocument?.name || 'Document'}</div>
                <div className={styles.fallback_text}>
                  Preview is not available for this file type yet. You can still open or download it using the toolbar.
                </div>
              </View>
            ) : null}
          </div>

        </View>
      </View>
    </View>
  );
};

export default DocumentPreviewComponent;
